import { useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, ArrowLeft, Play } from "lucide-react";
import { resolveMedia } from "@/lib/api";
import { vimeoId, youtubeId } from "@/lib/video";
import Picture from "@/components/Picture";

function VideoFrame({ item }) {
  const kind = item.video_kind || (item.video?.includes("vimeo") ? "vimeo" : item.video?.includes("youtu") ? "youtube" : "mp4");
  const src = item.video;
  if (kind === "vimeo") {
    const id = vimeoId(src);
    if (id) {
      return (
        <iframe
          key={item.id}
          src={`https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`}
          title={item.title}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="hl-lightbox-media"
          data-testid="lightbox-vimeo"
          style={{ width: "85vw", height: "calc(85vw * 9 / 16)", maxHeight: "85vh", aspectRatio: "16/9" }}
        />
      );
    }
  }
  if (kind === "youtube") {
    const id = youtubeId(src);
    if (id) {
      return (
        <iframe
          key={item.id}
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
          title={item.title}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="hl-lightbox-media"
          data-testid="lightbox-youtube"
          style={{ width: "85vw", height: "calc(85vw * 9 / 16)", maxHeight: "85vh", aspectRatio: "16/9" }}
        />
      );
    }
  }
  // Default: MP4 - plays with sound; browser may pause autoplay if no media-engagement, controls remain available
  return (
    <video
      key={item.id}
      src={resolveMedia(src)}
      poster={resolveMedia(item.image)}
      controls
      autoPlay
      playsInline
      className="hl-lightbox-media"
      data-testid="lightbox-video"
    />
  );
}

function RelatedGrid({ items, current, onChange }) {
  const related = items
    .filter((i) => i.id !== current.id)
    .sort((a, b) => (a.category === current.category ? 0 : 1) - (b.category === current.category ? 0 : 1))
    .slice(0, 6);

  if (related.length === 0) return null;

  return (
    <div className="mt-10" data-testid="lightbox-related">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-3">
        Related work
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {related.map((rel) => (
          <button
            key={rel.id}
            type="button"
            onClick={() => onChange(rel.id)}
            data-testid={`related-item-${rel.id}`}
            className="group relative aspect-[4/3] overflow-hidden bg-[#0a0a0a] text-left"
            aria-label={`Open ${rel.title}`}
          >
            <Picture
              item={rel}
              variant="thumbnail"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
            {rel.type === "video" && (
              <span className="absolute top-1.5 right-1.5 text-white/90">
                <Play size={12} fill="currentColor" />
              </span>
            )}
            <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="absolute left-2 right-2 bottom-1.5 text-[10px] text-white leading-tight truncate opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {rel.title}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Lightbox({ items, activeId, onClose, onChange }) {
  const scrollRef = useRef(null);
  const index = items.findIndex((i) => i.id === activeId);
  const item = index >= 0 ? items[index] : null;

  const goNext = useCallback(() => {
    if (!items.length) return;
    const next = (index + 1) % items.length;
    onChange(items[next].id);
  }, [index, items, onChange]);

  const goPrev = useCallback(() => {
    if (!items.length) return;
    const prev = (index - 1 + items.length) % items.length;
    onChange(items[prev].id);
  }, [index, items, onChange]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeId]);

  useEffect(() => {
    if (!item) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [item, onClose, goNext, goPrev]);

  if (!item) return null;

  return (
    <div
      className="hl-lightbox"
      data-testid="lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 sm:px-8 py-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="lightbox-back"
          className="flex items-center gap-2 text-white/80 hover:text-white text-[11px] tracking-[0.24em] uppercase transition-colors"
        >
          <ArrowLeft size={16} /> Back to gallery
        </button>
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          className="text-white/80 hover:text-white p-2 transition-colors"
          aria-label="Close lightbox"
        >
          <X size={22} />
        </button>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); goPrev(); }}
        data-testid="lightbox-prev"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/40 border border-white/15 backdrop-blur-md text-white flex items-center justify-center hover:bg-[#4A6D8C]/80 transition-all"
        aria-label="Previous"
      >
        <ChevronLeft size={22} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); goNext(); }}
        data-testid="lightbox-next"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/40 border border-white/15 backdrop-blur-md text-white flex items-center justify-center hover:bg-[#4A6D8C]/80 transition-all"
        aria-label="Next"
      >
        <ChevronRight size={22} />
      </button>

      <div
        ref={scrollRef}
        className="relative flex flex-col items-center w-full min-h-full py-20 sm:py-24 px-3 sm:px-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center w-full max-w-[1800px]">
          <div className="relative w-full flex items-center justify-center overflow-hidden">
            {item.type === "video" ? (
              <VideoFrame item={item} />
            ) : (
              <picture key={item.id} className="block w-full">
                {item.webp_image && (
                  <source srcSet={resolveMedia(item.webp_image)} type="image/webp" />
                )}
                <img
                  src={resolveMedia(item.image)}
                  alt={item.alt_text || item.title}
                  className="hl-lightbox-media is-image"
                  data-testid="lightbox-image"
                />
              </picture>
            )}
          </div>
          <div className="mt-8 w-full max-w-4xl text-white" data-testid="lightbox-info">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[10px] tracking-[0.24em] uppercase text-[#A3A3A3] border border-white/15 px-2 py-1">
                {item.category}
              </span>
              <span className="text-[10px] tracking-[0.24em] uppercase text-[#A3A3A3]">
                {item.type === "video" ? "Film" : "Photograph"}
              </span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
              {item.title}
            </h3>
            <div className="text-sm text-[#A3A3A3] mt-1">
              {item.client ? `Client · ${item.client}` : ""}
            </div>
            <div className="text-sm text-[#c9d1d9] mt-3 max-w-2xl leading-relaxed">
              {item.description}
            </div>

            {item.type !== "video" && item.technical?.behind_the_lens && (
              <div
                className="mt-3 max-w-2xl text-[13px] text-[#A3A3A3] italic leading-relaxed border-l-2 border-[#4A6D8C] pl-3"
                data-testid="lightbox-btl"
              >
                <span className="not-italic text-[10px] tracking-[0.28em] uppercase text-[#4A6D8C] block mb-1">
                  Behind the lens{item.technical.kit_used ? ` · ${item.technical.kit_used}` : ""}
                </span>
                {item.technical.behind_the_lens}
              </div>
            )}

            <RelatedGrid items={items} current={item} onChange={onChange} />

            <div className="text-[10px] tracking-[0.24em] uppercase text-[#525252] mt-4">
              {index + 1} / {items.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
